import { Component, ComponentInterface, Element, h, Host, JSX, Listen, Prop, Watch } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { SharedDirectionService } from '../../global/workbench-global';
import { DEFAULT_WORKBENCH_THEME, WorkbenchTheme } from '../../shared/themes';
import { getRandomId } from '../../utils/get-random-id';

/**
 * @slot - select element (required)
 * @slot message - Used to place a wb-control-hint or wb-control-error below the input (optional)
 * @slot label - Text placed within the inner label element (required)
 */
@Component({
  tag: 'wb-select',
  styleUrl: 'select.scss',
  shadow: true,
})
export class Select implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** @deprecated label for accessibility (deprecated in favor of the "label" slot) */
  @Prop()
  public label!: string;

  /** Displays compact variant */
  @Prop({ reflect: true })
  public compact: boolean = false;

  /** Marks input control as successful */
  @Prop({ reflect: true })
  public success: boolean = false;

  /** marks control as invalid. automatically set by slotted wb-control-error */
  @Prop({ reflect: true, mutable: true })
  public error: boolean = false;

  /** marks control as disabled. DON'T MANUALLY USE. Automatically set via slotted select */
  @Prop({ reflect: true, mutable: true })
  public disabled: boolean = false;

  /** The colour scheme */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme = DEFAULT_WORKBENCH_THEME;

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public active: boolean = false;

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public filled: boolean = false;

  /** @internal */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  /** label describing to a screenreader the state of a validation */
  @Prop({ reflect: true })
  public readerValidationMessage?: string = '';

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  private TagNames = transformTagNames(this, 'wb-select', ['wb-control-error', 'wb-control-hint', 'wb-icon', 'wb-visually-hidden']);

  private selectEl!: HTMLSelectElement;
  private mutationObserver: MutationObserver;
  private skipNextSlotChangeHandler: boolean = false;

  public connectedCallback(): void {
    this.active = Boolean(document.activeElement === this.selectEl);
  }

  private handleMessageSlotChange = (): void => {
    const messageEl = this.el.querySelector<HTMLWbControlErrorElement | HTMLWbControlHintElement>(`${this.TagNames.WbControlError},${this.TagNames.WbControlHint}`);

    if (messageEl) {
      messageEl.theme = this.theme;
      messageEl.id = messageEl.id || `message-${getRandomId()}`;
      this.selectEl.setAttribute('aria-describedby', messageEl.id);
    } else {
      this.selectEl.removeAttribute('aria-describedby');
    }

    this.error = Boolean(this.el.querySelector(this.TagNames.WbControlError));
  };

  private handleSlotChange = (): void => {
    if (this.skipNextSlotChangeHandler) {
      this.skipNextSlotChangeHandler = false;
      return;
    }

    this.selectEl = this.el.querySelector('select');

    if (!this.selectEl) {
      return;
    }

    this.updateDisabled();
    this.updateFilled();
    this.setSelectId();
    this.updateLightDomLabel();
    this.registerDisabledObserver();
    this.registerFocusEventListeners();
  };

  private setSelectId(): void {
    this.selectEl.id = this.selectEl.id || `select-${getRandomId()}`;
  }

  private registerDisabledObserver(): void {
    if (this.mutationObserver) {
      return;
    }

    this.mutationObserver = new MutationObserver(this.updateDisabled);
    this.mutationObserver.observe(this.selectEl, {
      subtree: false,
      attributeFilter: ['disabled'],
    });
  }

  private registerFocusEventListeners(): void {
    this.selectEl.addEventListener('focus', this.onFocus);
    this.selectEl.addEventListener('blur', this.onBlur);
    this.selectEl.addEventListener('change', this.updateFilled);
  }

  private updateFilled = (): void => {
    this.filled = Boolean(this.selectEl.value);
  };

  private updateDisabled = (): void => {
    this.disabled = this.selectEl.disabled;
  };

  private onFocus = (): void => {
    this.active = true;
  };

  private onBlur = (): void => {
    this.active = false;
  };

  @Watch('label')
  public updateLightDomLabel(): void {
    const labelEl = this.el.querySelector('label');

    if (labelEl) {
      labelEl.textContent = this.label;
    } else {
      const newLabelEl = document.createElement('label');
      newLabelEl.hidden = true;
      newLabelEl.textContent = this.label;
      newLabelEl.htmlFor = this.selectEl.id;

      this.skipNextSlotChangeHandler = true; // setting this to avoid the default slot change handler from running twice
      this.el.appendChild(newLabelEl);
    }
  }

  public render(): JSX.Element {
    const { WbIcon, WbVisuallyHidden } = this.TagNames;

    return (
      <Host class="wb-select">
        <div class="inner">
          <div class="inner-border">
            <div class="label">{this.label ? this.label : <slot name="label" />}</div>
            <slot onSlotchange={this.handleSlotChange} />
            <span aria-live="polite">
              {this.success && <WbIcon name="checkmark-circle" class="icon success-icon" />}
              {this.error && <WbIcon name="alert-circle" class="icon error-icon" />}
              {this.readerValidationMessage && <WbVisuallyHidden>{this.readerValidationMessage}</WbVisuallyHidden>}
            </span>
            <WbIcon name="bds/caret-down/24" class="icon caret-icon" />
          </div>
        </div>
        <slot name="message" onSlotchange={this.handleMessageSlotChange} />
      </Host>
    );
  }
}
